import { createAPIHooks } from '@lifeomic/one-query';
import { usePhcRestAxios } from '@lifeomic/phc-web-toolkit/src/hooks/useRestAPIHooks';

type MyOmeRequisitionStatus =
  | 'AWAITING_PARITY'
  | 'AWAITING_GATES'
  | 'PLACING'
  | 'OPEN'
  | 'REPORT_CREATED'
  | 'FAILED';

export type MyOmeMissingField = {
  name: string;
  source: 'survey' | 'lab-or-vital' | 'consent';
};

export type MyOmeRerunResponse = {
  outcome: 'placed' | 'waiting' | 'failed' | 'no-op';
  /** The row's status after the re-run. */
  status: MyOmeRequisitionStatus;
  noOpReason?: 'in-flight' | 'done' | 'not-rerunnable' | 'debounced';
  reasonCode?: string;
  /** GIS's free text. Not shown to clinicians. */
  reason?: string;
  failureClass?:
    | 'pre-post'
    | 'vendor-rejected'
    | 'vendor-unknown'
    | 'post-placement';
  missing?: MyOmeMissingField[];
  rqId?: string;
  lastRerunAt?: string;
  lastRerunBy?: string;
};

/**
 * genomic-ingestion-service routes, in the shape `@lifeomic/service-clients`
 * generates them. Replace with its `Endpoints` once a release declares the
 * re-run route (FLENG-176).
 */
type GenomicIngestionServiceEndpoints = {
  'POST /genomic-ingestion/projects/:projectId/myome/requisitions/:requisitionId/rerun': {
    // eslint-disable-next-line @typescript-eslint/ban-types
    Request: {};
    PathParams: { projectId: string; requisitionId: string };
    Response: MyOmeRerunResponse;
  };
};

/** service-clients keys GIS routes without its `/v1` base path. */
type WithV1BasePath<Endpoints> = {
  [Route in keyof Endpoints as Route extends `${infer Method} ${infer Path}`
    ? `${Method} /v1${Path}`
    : never]: Endpoints[Route];
};

export type GenomicIngestionEndpoints =
  WithV1BasePath<GenomicIngestionServiceEndpoints>;

const hooks = createAPIHooks<GenomicIngestionEndpoints>({
  name: 'genomic-ingestion-service',
  client: () => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return usePhcRestAxios();
  },
});

export const useGenomicIngestionMutation = hooks.useAPIMutation;
